import { CopyOutlined } from '@ant-design/icons'
import { Button, Tooltip } from 'antd'
import { createElement, ReactNode } from 'react'
import { NotificationPayload } from 'src/features/notifications'

type BudgetRequestError = {
  status?: number
  body?: {
    message?: string
  }
}

const backendErrorMessages: Record<string, string> = {
  'An article item with this name already exists in this article.':
    'Пункт статьи с таким названием уже существует в этой статье.',
  'costByCountry must contain at least one item when enabled.':
    'Добавьте хотя бы одну страну.',
  'costByCountry.countryCode must be an ISO-3 country code.':
    'Код страны должен быть трёхбуквенным кодом ISO.',
  'costByCountry countryCode values must be unique.':
    'Страны не должны повторяться.',
  'costByCountry.amount must be a positive decimal with no more than 18 decimal places.':
    'Укажите положительную сумму не более чем с 18 знаками после запятой.',
  'costByCountry.currency must be a valid currency code.':
    'Укажите корректный код валюты.',
  'amount must be a positive decimal with no more than 18 decimal places.':
    'Укажите положительную сумму не более чем с 18 знаками после запятой.',
  'currency must be a valid currency code.': 'Укажите корректный код валюты.',
}

const getBackendMessage = (error?: BudgetRequestError) =>
  error?.body?.message?.trim()

const getErrorDescription = (
  error: BudgetRequestError | undefined,
  fallback: string,
) => {
  const backendMessage = getBackendMessage(error)

  if (!backendMessage) return fallback

  return backendErrorMessages[backendMessage] ?? backendMessage
}

const getErrorDescriptionWithCopy = (
  error: BudgetRequestError | undefined,
  fallback: string,
): ReactNode => {
  const backendMessage = getBackendMessage(error)
  const description = getErrorDescription(error, fallback)

  if (!backendMessage) return description

  return createElement(
    'span',
    null,
    description,
    ' ',
    createElement(
      Tooltip,
      { title: 'Скопировать причину с сервера' },
      createElement(Button, {
        type: 'text',
        size: 'small',
        icon: createElement(CopyOutlined),
        style: { padding: 0, marginLeft: 4, verticalAlign: 'middle' },
        'aria-label': 'Скопировать причину с сервера',
        onClick: () => void navigator.clipboard?.writeText(backendMessage),
      }),
    ),
  )
}

export const getBudgetErrorNotification = (
  error?: BudgetRequestError,
): NotificationPayload => {
  if (error?.status === 401) {
    return {
      type: 'error',
      message: 'Необходимо войти в систему',
      description: 'Авторизуйтесь и повторите попытку.',
    }
  }

  if (error?.status === 403) {
    return {
      type: 'error',
      message: 'Действие недоступно: у вас нет прав для его выполнения',
      description: '',
    }
  }

  if (error?.status === 404) {
    return {
      type: 'error',
      message: 'Не удалось найти запрошенные данные',
      description: getErrorDescriptionWithCopy(
        error,
        'Обновите страницу и повторите попытку.',
      ),
    }
  }

  if (error?.status === 409) {
    return {
      type: 'error',
      message: 'Не удалось выполнить действие',
      description: getErrorDescriptionWithCopy(
        error,
        'Данные были изменены. Обновите страницу и повторите попытку.',
      ),
    }
  }

  if (error?.status === 422) {
    return {
      type: 'error',
      message: 'Не удалось выполнить действие',
      description: getErrorDescriptionWithCopy(
        error,
        'Проверьте заполненные данные и повторите попытку.',
      ),
    }
  }

  return {
    type: 'error',
    message: 'Не удалось выполнить запрос',
    description: getErrorDescriptionWithCopy(
      error,
      'Попробуйте ещё раз позже.',
    ),
  }
}
